import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { getParameterByName } from '@Root/utils/routeUtils'
import { useSessionStorage } from '@Root/utils/storageUtils'
import useBrowser from '@Hooks/myhouse/useBrowser'

const options = {
  profileList: ['local', 'test', 'etest', 'staging', 'dev', ''],
  pcPrefixDomainList: ['', 'new', 'auction1'],
  mobilePrefixDomainList: ['m', 'm2', 'fin', 'neo'],
  isale: {
    prefixDomain: 'isale',
    prefixPath: {
      pc: '/iSale/Map',
      mobile: '/NewiSaleMobile',
    },
  },
}

const useMainHeader = () => {
  const location = useLocation()
  const { isLandApp, isMobile } = useBrowser()
  const path = location.pathname
  const { getStorage, setStorage } = useSessionStorage()

  const saveSessionStorageAndReturn = (target: string, fix?: boolean) => {
    setStorage(['home', 'header', 'back', 'target'], target)
    setStorage(['home', 'header', 'back', 'mode'], fix ? 'fix' : '')
    return target
  }

  const homeBackTargetFromParameter = () => {
    const headerTargetParameter = getParameterByName('rurl')

    if (['pcland', 'land', 'pay'].includes(headerTargetParameter)) {
      return saveSessionStorageAndReturn(headerTargetParameter, true)
    }

    return ''
  }

  const homeBackTargetFromFixState = () => {
    // 2순위 파라미터 고정시 미변경
    const beforeTarget = getStorage(['home', 'header', 'back', 'target'])
    const fixMode = getStorage(['home', 'header', 'back', 'mode'])

    if (fixMode === 'fix') {
      return beforeTarget || homeBackTargetFromUserAgent()
    }
    return ''
  }

  const homeBackTargetFromReferrer = () => {
    const landDomainExp = /(https?:\/\/)([^\./]*)\.?(land.naver.com)/
    // 3순위 referrer
    if (!document.referrer || document.referrer === '') {
      // noreferrer
      return ''
    }

    if (document.referrer.includes('neo.land.naver.com')) {
      return ''
    }

    if (document.referrer.includes('pay.naver.com')) {
      if (document.referrer.includes('/pcpay')) {
        return saveSessionStorageAndReturn('pcland')
      }
      return saveSessionStorageAndReturn('pay')
    }

    if (!landDomainExp.test(document.referrer)) {
      return ''
    }

    const match = document.referrer.match(landDomainExp)
    const type = match?.[2] || ''
    const typeSplit = type.split('-')
    const typeTarget = typeSplit.length === 2 ? typeSplit?.[1] : typeSplit?.[0]

    // typeTarget이 profile인 경우 service
    if (options.profileList.includes(typeTarget)) {
      return saveSessionStorageAndReturn('pcland')
    }

    if (typeTarget === options.isale.prefixDomain) {
      if (document.referrer.includes(options.isale.prefixPath.pc)) {
        return saveSessionStorageAndReturn('pcland')
      }
      return saveSessionStorageAndReturn('land')
    }

    if (options.mobilePrefixDomainList.includes(typeTarget)) {
      return saveSessionStorageAndReturn('land')
    }

    return ''
  }

  const homeBackTargetFromUserAgent = () => {
    if (isLandApp || isMobile) {
      return 'land'
    }
    return 'pcland'
  }

  /**
   * @remarks
   * 1. queryString : rurl only 'land', 'pay'. referrer can't overwrite
   * 2. state rurl fix. not changed.
   * 3. referrer
   * 3-1. noreferrer : not changed.
   * 3-2. neo.land.naver.com : not changed.
   * 3-3. *.pay.naver.com : pay
   * 3-3-1. include '/pcpay' path : pcland
   * 3-3-2. exclude '/pcpay' path: pay
   * 3-4. *isale.land.naver.com
   * 3-4-1. include '/iSale/Map' path : pcland
   * 3-4-2. exclude '/iSale/Map' path : land
   * 3-5. *.land.naver.com
   * 3-5-1. mobile domain : land.
   * 3-5-2. other : pcland.
   * 4. beforeTarget
   * 5. userAgent
   * 5-1. isLandApp or isMobile : land
   * 5-2. other(with ipad) : pcland
   *
   * 모바일에서 noreferrer로 넘어오는 경우가 발생할 경우 매우 취약합니다.
   * 기타 상황의 경우 land.naver.com로 보내고 useragent 필터링을 통해 모바일 or PC버전으로 분기되도록 합니다.
   *
   * @returns 'pcland' | 'land' | 'pay'
   */
  const homeBackTarget = useMemo(() => {
    const beforeTarget = getStorage(['home', 'header', 'back', 'target'])
    return homeBackTargetFromParameter() || homeBackTargetFromFixState() || homeBackTargetFromReferrer() || beforeTarget || homeBackTargetFromUserAgent()
  }, [path])

  return { homeBackTarget }
}

export default useMainHeader
